import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { supabase } from '../lib/supabase';
import {
  MessageCircle,
  ExternalLink,
  Home,
  DollarSign,
  Target,
  Calendar,
  FileText,
  Zap,
  ChevronDown,
  Check
} from 'lucide-react';

const PHONE = '5521977349631';

type SupportCard = {
  key: string;
  title: string;
  desc: string;
  icon: React.ReactNode;
};

const cards: SupportCard[] = [
  { key: 'visao-geral', title: 'Visão Geral', desc: 'Dúvidas sobre o painel inicial e indicadores.', icon: <Home size={22} /> },
  { key: 'transacoes', title: 'Transações', desc: 'Ajuda com lançamentos, categorias e importações.', icon: <DollarSign size={22} /> },
  { key: 'metas', title: 'Metas', desc: 'Suporte para criação e acompanhamento de metas.', icon: <Target size={22} /> },
  { key: 'agenda', title: 'Agenda', desc: 'Agendamentos, lembretes e sincronizações.', icon: <Calendar size={22} /> },
  { key: 'relatorios', title: 'Relatórios', desc: 'Geração, exportação e interpretação de relatórios.', icon: <FileText size={22} /> },
  { key: 'suporte-geral', title: 'Suporte Geral', desc: 'Atendimento geral, dúvidas e feedbacks.', icon: <MessageCircle size={22} /> }
];

const buildWhatsAppLink = (keyword: string) => {
  const text = `Olá! Preciso de suporte especializado na página: ${keyword} (palavra-chave: ${keyword})`;
  return `https://wa.me/${PHONE}?text=${encodeURIComponent(text)}`;
};

const WhatsApp: React.FC = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [subscriptionPlan, setSubscriptionPlan] = useState<string | null>(null);
  const [showEspecializado, setShowEspecializado] = useState(true);
  const [consent, setConsent] = useState(false);

  useEffect(() => {
    const loadPlan = async () => {
      try {
        const isSupabaseConfigured = import.meta.env.VITE_SUPABASE_URL?.includes('supabase.co') && 
                                     import.meta.env.VITE_SUPABASE_ANON_KEY?.length > 50;
        if (!isSupabaseConfigured) return;

        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        const { data, error } = await supabase
          .from('profiles')
          .select('subscription_plan')
          .eq('user_id', user.id)
          .single();

        if (data && data.subscription_plan) {
          setSubscriptionPlan(data.subscription_plan);
        }
      } catch (err) {
        console.error('Erro carregando plano do perfil:', err);
      }
    };

    loadPlan();
  }, []);

  const consentWarning = useMemo(() => !consent ? 'Confirme a autorização para prosseguir.' : '', [consent]);

  return (
    <div className={`${theme === 'dark' ? 'bg-slate-900 text-white' : 'bg-gradient-to-br from-white via-emerald-50 to-slate-50 text-gray-900'} min-h-screen p-6`}>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <header className="flex items-center justify-between gap-4 mb-8 flex-wrap">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-lg shadow-md bg-gradient-to-br from-emerald-500 to-emerald-400 text-white">
              <Zap size={28} />
            </div>
            <div>
              <h1 className="text-2xl font-extrabold">Suporte por WhatsApp</h1>
              <p className="text-sm opacity-80">Selecione suporte geral ou escolha uma área especializada.</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate('/dashboard')}
              className={`hidden sm:inline-flex items-center gap-2 px-4 py-2 rounded-lg border ${theme === 'dark' ? 'border-slate-700 bg-slate-800' : 'border-gray-200 bg-white'} shadow-sm hover:shadow-md`}
            >
              <ExternalLink size={16} /> Voltar
            </button>
            <a
              href={buildWhatsAppLink('suporte-geral')}
              target="_blank"
              rel="noreferrer"
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-emerald-600 to-emerald-500 text-white shadow-lg ${!consent ? 'opacity-70 cursor-not-allowed' : 'hover:shadow-emerald-400/40'}`}
              onClick={(e) => {
                if (!consent) e.preventDefault();
              }}
            >
              <MessageCircle size={16} /> Suporte Geral
            </a>
          </div>
        </header>

        {/* Intro card */}
        <div className={`rounded-2xl p-6 mb-8 shadow-lg ${theme === 'dark' ? 'bg-slate-850' : 'bg-white/90'} border border-emerald-100/40`}> 
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="space-y-1">
              <h2 className="text-lg font-bold">Atendimento especializado por página</h2>
              <p className="text-sm opacity-80">Botões abaixo já abrem o WhatsApp com mensagem pronta. A palavra-chave aparece na mensagem para priorizar seu atendimento.</p>
              <label className="flex items-center gap-2 text-sm mt-2 select-none">
                <input
                  type="checkbox"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  className="accent-emerald-500 w-4 h-4"
                />
                <span>Autorizo ser contatado via WhatsApp para suporte relacionado a esta solicitação.</span>
              </label>
              {!consent && (
                <p className="text-xs text-amber-500">{consentWarning}</p>
              )}
            </div>

            <div className="flex flex-wrap gap-2 text-sm">
              <button
                onClick={() => setShowEspecializado(false)}
                className={`px-3 py-2 rounded-lg border ${!showEspecializado ? 'bg-emerald-600 text-white border-emerald-600' : 'border-emerald-200 text-emerald-800 bg-emerald-50'}`}
              >
                Rápido
              </button>
              <button
                onClick={() => setShowEspecializado(true)}
                className={`px-3 py-2 rounded-lg border flex items-center gap-1 ${showEspecializado ? 'bg-slate-900 text-white border-slate-800' : 'border-slate-200 text-slate-800 bg-white'}`}
              >
                Especializado
                <ChevronDown size={14} />
              </button>
            </div>
          </div>
        </div>

        {/* Cards Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {(showEspecializado ? cards.filter(c => c.key !== 'suporte-geral') : cards.filter(c => c.key === 'suporte-geral')).map(c => (
            <article key={c.key} className={`group relative overflow-hidden rounded-2xl p-6 shadow-lg transition-all hover:-translate-y-1 ${theme === 'dark' ? 'bg-slate-850' : 'bg-white/95 border border-slate-100'}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-4">
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${theme === 'dark' ? 'bg-slate-700/70' : 'bg-emerald-50'}`}>{c.icon}</div>
                  <div>
                    <h3 className="text-lg font-semibold">{c.title}</h3>
                    <p className="text-sm opacity-80 mt-1">{c.desc}</p>
                  </div>
                </div>
                {showEspecializado && (
                  <div className="text-xs px-2 py-1 rounded-md bg-emerald-50 text-emerald-700 border border-emerald-100">Especializado</div>
                )}
              </div>

              <div className="mt-6 flex items-center justify-between gap-3">
                <a
                  href={buildWhatsAppLink(c.title)}
                  target="_blank"
                  rel="noreferrer"
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-emerald-600 to-emerald-500 text-white shadow-sm ${!consent ? 'opacity-70 cursor-not-allowed' : 'hover:shadow-emerald-400/40'}`}
                  onClick={(e) => {
                    if (!consent) e.preventDefault();
                  }}
                >
                  Abrir no WhatsApp
                </a>

                <button
                  onClick={() => navigator.clipboard?.writeText(c.key)}
                  title={`Copiar palavra-chave: ${c.key}`}
                  className={`px-3 py-2 rounded-md text-sm border ${theme === 'dark' ? 'border-slate-700' : 'border-gray-200'} hover:shadow ${!consent ? 'opacity-60 cursor-not-allowed' : ''}`}
                  disabled={!consent}
                >
                  Copiar palavra-chave
                </button>
              </div>
            </article>
          ))}
        </div>

        {/* Footer small */}
        <footer className="mt-10 text-center text-sm opacity-80 space-y-2">
          <div className="flex items-center justify-center gap-2 text-xs">
            <div className={`w-4 h-4 rounded-sm border flex items-center justify-center ${consent ? 'bg-emerald-500 border-emerald-500 text-white' : 'border-slate-300 text-transparent'}`}>
              <Check size={12} />
            </div>
            <span>Ao prosseguir, você confirma que podemos responder via WhatsApp e que a mensagem será enviada para +55 21 97734-9631.</span>
          </div>
          <p className="text-xs text-slate-400">Precisa de outra área? Peça para incluir novas páginas ou palavras-chave.</p>
        </footer>
      </div>
    </div>
  );
};

export default WhatsApp;
